import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon, CreateNewFolder as FolderIcon } from '@mui/icons-material';
import { folderService } from '../../services/folderService';
import { useSnackbar } from 'notistack';

export interface NewFolderDialogProps {
  open: boolean;
  onClose: () => void;
  parentId: string | undefined;
  onFolderCreated?: () => void;
}

const NewFolderDialog: React.FC<NewFolderDialogProps> = ({ 
  open, 
  onClose, 
  parentId,
  onFolderCreated 
}) => {
  const [folderName, setFolderName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFolderName(e.target.value);
    if (error) setError('');
  };

  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      setError('Folder name is required');
      return;
    }

    setIsCreating(true);
    try {
      await folderService.createFolder(folderName.trim(), parentId);
      enqueueSnackbar('Folder created successfully', { variant: 'success' });
      if (onFolderCreated) {
        onFolderCreated();
      }
      handleClose();
    } catch (error: any) {
      setError(error.message || 'Failed to create folder');
      enqueueSnackbar('Failed to create folder', { variant: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setFolderName('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Create New Folder</Typography>
          </Box>
          <IconButton edge="end" color="inherit" onClick={handleClose} disabled={isCreating}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="name"
          label="Folder Name"
          type="text"
          fullWidth
          variant="outlined"
          value={folderName}
          onChange={handleChangeName}
          error={!!error}
          helperText={error}
          disabled={isCreating}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleCreateFolder();
            }
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateFolder}
          disabled={isCreating || !folderName.trim()}
          startIcon={isCreating ? <CircularProgress size={20} /> : <FolderIcon />}
        >
          {isCreating ? 'Creating...' : 'Create Folder'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewFolderDialog; 